import { styled } from '@mui/system';
import { Box, Typography, Alert, Button, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import SpaIcon from '@mui/icons-material/Spa';
import { colors, fontFamilies } from '../../constants/theme';

export const Root = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$isModal',
})(({ theme, $isModal }) => ({
  width: '100%',
  maxWidth: $isModal ? '600px' : '700px',
  margin: '0 auto',
  padding: $isModal ? theme.spacing(2, 2, 3) : theme.spacing(8),
  boxShadow: $isModal
    ? 'none'
    : `
    -4px -4px 17.8px 0px rgba(205, 214, 223, 0.15),
     4px  4px 15px   0px rgba(205, 214, 223, 0.25)
  `,
  borderRadius: $isModal ? '0' : '16px',
  backgroundColor: $isModal ? 'transparent' : colors.whiteText,
}));

/* ── Header section ── */

export const HeaderSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

export const IconCircle = styled(Box)(() => ({
  width: 72,
  height: 72,
  borderRadius: '50%',
  backgroundColor: colors.lighthBackground,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: 16,
}));

export const LockIcon = styled(LockOutlinedIcon)(() => ({
  fontSize: 34,
  color: colors.secondary,
}));

export const Title = styled(Typography)(({ theme }) => ({
  fontFamily: fontFamilies.gotham,
  fontSize: 'clamp(22px, 4vw, 32px)',
  fontWeight: 400,
  color: colors.primary,
  textAlign: 'center',
  letterSpacing: '0.01em',
  marginBottom: theme.spacing(1),
}));

export const Subtitle = styled(Typography)(({ theme }) => ({
  fontFamily: fontFamilies.gotham,
  fontSize: 'clamp(16px, 3vw, 22px)',
  fontWeight: 400,
  color: colors.secondary,
  textAlign: 'center',
  marginBottom: theme.spacing(2),
}));

/* ── Lotus divider ── */

export const DividerRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  maxWidth: 560,
  marginTop: 0,
  marginRight: 'auto',
  marginBottom: theme.spacing(2.5),
  marginLeft: 'auto',
  gap: theme.spacing(1.5),
}));

export const DividerLine = styled(Box)(() => ({
  flex: 1,
  height: 1,
  backgroundColor: colors.lightBorder,
  opacity: 0.6,
}));

export const LotusIcon = styled(SpaIcon)(() => ({
  fontSize: 20,
  color: colors.lightBorder,
  flexShrink: 0,
}));

/* ── Body copy ── */

export const BodyBlock = styled(Typography)(({ theme }) => ({
  fontFamily: fontFamilies.quicksand,
  fontSize: 'clamp(14px, 2vw, 16px)',
  lineHeight: 1.7,
  color: colors.primary,
  textAlign: 'center',
  maxWidth: 560,
  margin: `0 auto ${theme.spacing(3)}`,
  '& strong': {
    fontWeight: 700,
    color: colors.primary,
  },
}));

/* ── Success state ── */

export const SuccessBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(2),
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}));

export const SuccessIcon = styled(CheckCircleIcon)(() => ({
  fontSize: '80px',
  color: '#4caf50',
}));

export const SuccessTitle = styled(Typography)(() => ({
  fontFamily: fontFamilies.quicksand,
  fontSize: '18px',
  fontWeight: 600,
  color: colors.primary,
  textAlign: 'center',
}));

/* ── Error alert ── */

export const ErrorAlert = styled(Alert)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  maxWidth: 560,
  margin: `0 auto ${theme.spacing(2)}`,
}));

/* ── Card input area ── */

export const Form = styled(Box)(() => ({
  position: 'relative',
}));

export const CardContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '60px',
}));

export const StripeMountPoint = styled(Box)(() => ({
  width: '100%',
  maxWidth: 540,
  minHeight: '54px',
  border: `1px solid ${colors.lightBorder}`,
  borderRadius: '12px',
  padding: '14px 18px',
  boxSizing: 'border-box',
  backgroundColor: '#fff',
}));

export const CardLoadingOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(2),
  backgroundColor: 'rgba(255,255,255,0.85)',
  borderRadius: '12px',
}));

export const LoadingStateBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

export const StyledCircularProgress = styled(CircularProgress)(() => ({
  color: colors.secondary,
}));

export const LoadingText = styled(Typography)(() => ({
  fontFamily: fontFamilies.quicksand,
  fontSize: '14px',
  color: colors.textLight,
}));

/* ── Stripe security note ── */

export const StripeSecurityNote = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(0.75),
  marginBottom: theme.spacing(3),
}));

export const StripeSecurityText = styled(Typography)(() => ({
  fontFamily: fontFamilies.quicksand,
  fontSize: '13px',
  color: colors.secondary,
  opacity: 0.75,
}));

export const SmallLockIcon = styled(LockOutlinedIcon)(() => ({
  fontSize: 14,
  color: colors.secondary,
  opacity: 0.75,
}));

/* ── Buttons ── */

export const ButtonGroup = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$embedded',
})(({ theme, $embedded }) => ({
  display: 'flex',
  justifyContent: $embedded ? 'space-between' : 'center',
  gap: theme.spacing(2),
  '@media (max-width: 600px)': {
    flexDirection: 'column',
  },
}));

export const BackButton = styled(Button)(() => ({
  fontFamily: fontFamilies.quicksand,
  fontSize: '16px',
  fontWeight: 600,
  padding: '13px 40px',
  textTransform: 'none',
  borderRadius: '10px',
  borderColor: '#492A18',
  color: '#492A18',
  minWidth: 140,
  '&:hover': {
    borderColor: '#492A18',
    backgroundColor: 'rgba(73, 42, 24, 0.04)',
  },
}));

export const SubmitButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== '$embedded',
})(({ $embedded }) => ({
  backgroundColor: colors.secondary,
  color: '#FFF',
  fontFamily: fontFamilies.quicksand,
  fontSize: '16px',
  fontWeight: 600,
  padding: '13px 40px',
  textTransform: 'none',
  borderRadius: '10px',
  minWidth: 140,
  flex: $embedded ? 'none' : 1,
  maxWidth: $embedded ? 'none' : '100%',
  '&:hover': {
    backgroundColor: '#1f3147',
  },
  '&:disabled': {
    backgroundColor: '#ccc',
  },
}));

/* ── Loading overlay ── */

export const Overlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  borderRadius: '16px',
  zIndex: 10,
}));

export const OverlayText = styled(Typography)(() => ({
  fontFamily: fontFamilies.quicksand,
  fontSize: '16px',
  color: colors.primary,
}));

/* ── Dialog paper ── */

export const DialogPaperStyles = {
  borderRadius: '16px',
  p: 2,
};

/* ── Legacy Subtext (kept for reference; use BodyBlock instead) ── */
export const Subtext = BodyBlock;
